import React from 'react';
import { Text, Alert, Table, Stack, Grid, ScrollArea } from '@good/ui/core';
import { TeamMember } from 'interfaces/team-member-interface';
import moment from 'moment-timezone';

interface FutureShift {
  supportWorkerAttendanceId: string;
  serviceDateTimeId: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  serviceName: string;
  serviceType: string;
  formattedDate: string;
  formattedTime: string;
}

interface IPermanentlyRemoveUserModalStep2ImpactedShiftsProps {
  worker: TeamMember;
  impactedShifts: FutureShift[];
  removalDate: string;
}

export const PermanentlyRemoveUserModalStep2ImpactedShifts: React.FC<
  IPermanentlyRemoveUserModalStep2ImpactedShiftsProps
> = ({ worker, impactedShifts, removalDate }) => {
  const name = `${worker.firstName} ${worker.lastName}`;
  const formattedRemovalDate = moment(removalDate).format('DD/MM/YYYY');

  const tableData = impactedShifts.map((shift) => ({
    id: shift.supportWorkerAttendanceId,
    date: shift.formattedDate,
    time: shift.formattedTime,
    service: shift.serviceName,
  }));

  return (
    <Stack gap='xs'>
      <Alert color='red' variant='light' p='xs'>
        {impactedShifts.length > 0
          ? `${name} will be removed from ${impactedShifts.length} future shift${impactedShifts.length !== 1 ? 's' : ''}`
          : `${name} has no future shifts after ${formattedRemovalDate}`}
      </Alert>

      <Grid>
        <Grid.Col span={6}>
          <Stack gap='xs'>
            <Text size='xs' fw={600}>
              SCHEDULED REMOVAL DATE
            </Text>
            <Text size='xs'>{formattedRemovalDate}</Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={6}>
          <Stack gap='xs'>
            <Text size='xs' fw={600}>
              TEAM MEMBER
            </Text>
            <Text size='xs'>{name}</Text>
          </Stack>
        </Grid.Col>
      </Grid>

      {impactedShifts.length > 0 ? (
        <Stack gap='xs'>
          <Text size='xs' fw={600}>
            IMPACTED SHIFTS ({impactedShifts.length})
          </Text>
          <ScrollArea h={150}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th py='xs'>Date</Table.Th>
                  <Table.Th py='xs'>Time</Table.Th>
                  <Table.Th py='xs'>Service</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tableData.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td py='xs'>{row.date}</Table.Td>
                    <Table.Td py='xs'>{row.time}</Table.Td>
                    <Table.Td py='xs'>{row.service}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Stack>
      ) : (
        <Alert color='gray' variant='light' p='xs'>
          No future shifts found. This team member has no scheduled shifts after the removal date.
        </Alert>
      )}

      <Alert color='blue' variant='light' p='xs'>
        {impactedShifts.length > 0
          ? 'These shifts will be unassigned immediately, giving you time to find replacement team members. The team member will remain active until the scheduled removal date.'
          : 'The team member will remain active until the scheduled removal date. No shifts need to be reassigned.'}
      </Alert>
    </Stack>
  );
};
